<script setup lang="ts">
import { ref, markRaw, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTicketStore } from '@/stores/useTicketStore'
import type { Ticket } from '@/stores/useTicketStore'
import TicketList from '@/components/features/tickets/TicketList.vue'
import TicketCreateModal from '@/components/features/tickets/TicketCreateModal.vue'
import {
  ArrowPathIcon,
  PlusIcon,
  ChevronLeftIcon,
  InboxIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/vue/24/outline'
import TicketDetail from '@/components/features/tickets/TicketDetail.vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'

const route = useRoute()
const router = useRouter()
const ticketStore = useTicketStore()
const isCreateModalOpen = ref(false)
const selectedTickets = ref<Ticket[]>([])

const searchQuery = ref('')
const selectedStatuses = ref<string[]>(['NEW', 'OPEN', 'WAITING', 'PAUSED', 'RESOLVED'])

// `active` = classes appliquées quand le statut est sélectionné (filtre on).
// Reprend la palette des badges de la liste pour rester cohérent d'un écran à l'autre.
const statusOptions = [
  { id: 'NEW', text: 'Nouveau', active: 'border-blue-300 bg-blue-100 text-blue-800' },
  { id: 'OPEN', text: 'Ouvert', active: 'border-green-300 bg-green-100 text-green-800' },
  { id: 'WAITING', text: 'En attente', active: 'border-orange-300 bg-orange-100 text-orange-800' },
  { id: 'PAUSED', text: 'En pause', active: 'border-gray-300 bg-gray-200 text-gray-800' },
  { id: 'RESOLVED', text: 'Résolu', active: 'border-teal-300 bg-teal-100 text-teal-800' },
  { id: 'CLOSED', text: 'Fermé', active: 'border-red-300 bg-red-100 text-red-800' },
]

const arrowPathIcon = markRaw(ArrowPathIcon)
const plusIcon = markRaw(PlusIcon)
const chevronLeftIcon = markRaw(ChevronLeftIcon)

const chipBase =
  'inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium transition-colors ' +
  'cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'

function chipClass(opt: { id: string; active: string }) {
  return selectedStatuses.value.includes(opt.id)
    ? `${chipBase} ${opt.active}`
    : `${chipBase} border-base-300 bg-base-100 text-base-content/70 hover:bg-base-200`
}

function toggleStatus(id: string) {
  const i = selectedStatuses.value.indexOf(id)
  if (i === -1) selectedStatuses.value.push(id)
  else selectedStatuses.value.splice(i, 1)
}

watch(
  () => route.params.ticketId,
  async (ticketId) => {
    if (ticketId && typeof ticketId === 'string') {
      await ticketStore.fetchTicketById(ticketId)
    } else {
      ticketStore.$patch({ ticket: null })
      await handleRefresh()
    }
  },
  { immediate: true },
)

async function handleRefresh() {
  await ticketStore.fetchAllTickets({
    search: searchQuery.value,
    statuses: selectedStatuses.value,
  })
}

// Watch for filter changes to trigger re-fetch
let refreshTimeout: ReturnType<typeof setTimeout> | undefined = undefined
function debouncedRefresh() {
  if (refreshTimeout !== undefined) clearTimeout(refreshTimeout)
  refreshTimeout = setTimeout(() => {
    handleRefresh()
  }, 500)
}

watch(searchQuery, () => {
  debouncedRefresh()
})

watch(
  selectedStatuses,
  () => {
    debouncedRefresh()
  },
  { deep: true },
)

function openCreateModal() {
  isCreateModalOpen.value = true
}

function closeCreateModal() {
  isCreateModalOpen.value = false
}

async function handleCreateTicket(ticket: { title: string; description: string; files: File[] }) {
  try {
    await ticketStore.createTicket(ticket)

    closeCreateModal()

    await handleRefresh()
  } catch (error) {
    console.error('Error creating ticket:', error)
  }
}

function handleSelectionChanged(tickets: Ticket[]) {
  selectedTickets.value = tickets
}

async function goBackToList() {
  ticketStore.$patch({ ticket: null })
  router.push('/support-interne')
  await handleRefresh()
}
</script>

<template>
  <div class="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 md:p-8">
    <!-- ── Mode liste ──────────────────────────────────────────────────── -->
    <template v-if="!ticketStore.ticket">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-3">
            <Bs2Title title="Support interne" />
            <span
              v-if="!ticketStore.loading"
              class="badge badge-ghost badge-sm font-medium tabular-nums"
            >
              {{ ticketStore.tickets.length }} ticket(s)
            </span>
          </div>
          <p class="m-0 text-sm text-base-content/70">
            Suivez, filtrez et créez les demandes de support internes.
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <ButtonDaisy
            text="Recharger"
            variant="ghost"
            :icon="arrowPathIcon"
            @click="handleRefresh"
            :disabled="ticketStore.loading"
          />
          <ButtonDaisy text="Créer un ticket" variant="hub" :icon="plusIcon" @click="openCreateModal" />
        </div>
      </header>

      <!-- Barre de filtres — liseré corail (couleur de section, cf MASTER §2) -->
      <section
        class="card border border-base-300 border-t-2 bg-base-100 shadow-sm"
        :style="{ borderTopColor: 'var(--hub-accent)' }"
      >
        <div class="card-body flex flex-col gap-4 p-4 md:p-6">
          <label class="form-control w-full max-w-xs">
            <span class="mb-1 text-sm font-medium text-base-content">Rechercher</span>
            <div class="relative">
              <MagnifyingGlassIcon
                class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-base-content/50"
                aria-hidden="true"
              />
              <input
                v-model="searchQuery"
                type="search"
                placeholder="Titre, référence…"
                class="input input-bordered w-full pl-9"
              />
              <button
                v-if="searchQuery"
                type="button"
                class="absolute inset-y-0 right-2 flex items-center text-base-content/50 transition-colors hover:text-error"
                aria-label="Effacer la recherche"
                @click="searchQuery = ''"
              >
                <XMarkIcon class="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </label>

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-base-content">Statuts</span>
            <div class="flex flex-wrap gap-2" role="group" aria-label="Filtrer par statut">
              <button
                v-for="opt in statusOptions"
                :key="opt.id"
                type="button"
                :class="chipClass(opt)"
                :aria-pressed="selectedStatuses.includes(opt.id)"
                @click="toggleStatus(opt.id)"
              >
                {{ opt.text }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Contenu : liste / vide / chargement détail -->
      <section class="card border border-base-300 bg-base-100 shadow-sm">
        <div class="card-body p-2 md:p-4">
          <div
            v-if="route.params.ticketId && ticketStore.loading"
            class="flex flex-col items-center justify-center gap-4 px-8 py-16 text-center text-base-content/70"
          >
            <ArrowPathIcon class="h-12 w-12 animate-spin" aria-hidden="true" />
            <p class="m-0 text-base">Chargement du ticket…</p>
          </div>

          <div
            v-else-if="ticketStore.tickets.length === 0"
            class="flex flex-col items-center justify-center gap-4 px-8 py-16 text-center text-base-content/70"
          >
            <InboxIcon class="h-12 w-12" aria-hidden="true" />
            <p class="m-0 text-base">Aucun ticket ne correspond à ces filtres.</p>
            <ButtonDaisy
              text="Créer un ticket"
              variant="outline"
              :icon="plusIcon"
              @click="openCreateModal"
            />
          </div>

          <TicketList
            v-else
            :tickets="ticketStore.tickets"
            :loading="ticketStore.loading"
            @selectionChanged="handleSelectionChanged"
          />
        </div>
      </section>
    </template>

    <!-- ── Mode détail ─────────────────────────────────────────────────── -->
    <template v-else>
      <ButtonDaisy
        text="Retour à la liste"
        variant="ghost"
        :icon="chevronLeftIcon"
        @click="goBackToList"
      />
      <section
        class="card border border-base-300 border-t-2 bg-base-100 shadow-sm"
        :style="{ borderTopColor: 'var(--hub-accent)' }"
      >
        <div class="card-body p-4 md:p-6">
          <TicketDetail :ticket="ticketStore.ticket" />
        </div>
      </section>
    </template>

    <TicketCreateModal :isOpen="isCreateModalOpen" @close="closeCreateModal" @create="handleCreateTicket" />
  </div>
</template>

<style scoped></style>
